import React, { useEffect } from "react";
import styles from "./dashboard-layout.module.css";
import { useUserContext } from "@/contexts/UserContext";
import { useLocation, useSearch } from "wouter";
import Button from "@/components/forms/Button/Button";
import { UserRole, UserStatus } from "@boxem/core-api-schema";
import { resendVerifyEmail } from "@/api/auth";
import NewSidebar from "@/app/navigation/sidebar/Sidebar";
import TextLogo from "@/components/TextLogo/TextLogo";

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
  const { user, hasTriedToGetUserFromLocalStorage, logout } = useUserContext();
  const [location, navigate] = useLocation();

  //Ensures admins don't navigate to pages they don't have data for.
  useEffect(() => {
    if (user?.role === UserRole.ADMIN || user?.role === UserRole.SUPPORT) navigate("~/admin/users");
  }, [user]);

  useEffect(() => {
    if (!user && hasTriedToGetUserFromLocalStorage) {
      return navigate(`~/auth/login?path=${encodeURIComponent(location)}`);

    } else if (!!user) {
      if (user.status === UserStatus.INCOMPLETE) {
        return navigate("~/auth/payment-information");
      } else if (!user.emailVerified) {
        // if we have a non-verified user and want to re-direct them to the verify-email-sent page,
        // as a convienence, just send them a confirmation again. This makes it so they don't have to click
        // 'resend'.
        resendVerifyEmail();

        return navigate("~/auth/verify-email");
      }
    }
  }, [user, hasTriedToGetUserFromLocalStorage, navigate]);

  if (!user) {
    return <></>
  }

  return (
    <>
      <div className={styles.mobileNotSupportedContainer}>
        <div className={styles.logo}>
          <TextLogo />
        </div>
        <div className={styles.notSupportedMessageContainer}>
          <div className={styles.notSupportedMessageText}>
            Boxem does not currently support mobile.
          </div>
          <div className={styles.notSupportedMessageText}>
            We're working on this functionality and we hope to release it soon.
          </div>
          <div className={styles.notSupportedMessageText}>
            Please login on a desktop or laptop to use boxem.
          </div>
        </div>
        <Button type="primary" className={styles.logoutButton} onClick={logout}>
          Log out
        </Button>
      </div>
      <div className={styles.container}>
        <NewSidebar />
        <main className={styles.main}>
          <div className={styles.pageContainer}>{children}</div>
        </main>
      </div>
    </>
  );
}
